import { 
    Component, 
    afterNextRender, 
    OnDestroy, 
    ElementRef, 
    ViewChild, 
    Renderer2, 
    inject,
    ChangeDetectorRef,
    NgZone
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { AppConfigService, Log } from '@core/services/config/app-config.service';
import { fromEvent, timer, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SlideConfigService } from './slide-config.service';
import { GallerySideImage, GallerySlideContent } from './gallery-data';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';

@Component({
    selector: 'app-slide',
    templateUrl: './slide.component.html',
    styleUrls: ['./slide.component.scss'],
    standalone: true,
    // IMPORTANTE: Skip hydration per questo componente problematico
    /* host: {
        '[attr.ngSkipHydration]': 'true'
    } */
})
export class SlideComponent implements OnDestroy {
    @ViewChild('mainImageContainer') mainImageContainer!: ElementRef;
    @ViewChild('currentGalleryImage') currentGalleryImage!: ElementRef<HTMLImageElement>;
    @ViewChild('overlayContent') overlayContentRef!: ElementRef;

    private destroy$ = new Subject<void>();
    private appConfig = inject(AppConfigService);
    private sanitizer = inject(DomSanitizer);
    private ngZone = inject(NgZone);
    private renderer = inject(Renderer2);
    private el = inject(ElementRef);
    private cdr = inject(ChangeDetectorRef);
    private router = inject(Router);
    private document = inject(DOCUMENT);

    // FIX 1: Tipizzazione corretta per subscription
    private autoSlideSubscription?: any;
    private resizeSubscription?: any;
    private imageLoadSubscription?: any;

    slides: GallerySlideContent[] = inject(SlideConfigService).getSlides();

    currentIndex: number = 0;

    // Banner standard della colonna di destra, usati quando la slide non ne imposta di propri
    private readonly defaultSideImages: GallerySideImage[] = [
        { src: 'images/home_gallery/banner-1.webp', link: null },
        { src: 'images/home_gallery/banner-2a.webp', link: null }
    ];
    autoSlideIntervalMs: number = 5000;
    // LCP: renderizziamo il contenuto dell'overlay della PRIMA slide già in SSR
    // (è l'elemento LCP della home: il testo `.slide-ricambi-originali`). Prima veniva
    // popolato solo lato client dopo hydration + load immagine → LCP ~9.9s (render delay 93%).
    // Il valore è identico tra SSR e primo render client (stessa slide 0, i18n già risolto in
    // SlideConfigService) quindi non introduce mismatch di hydration.
    processedHtmlContent: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(
        this.slides?.[0]?.html_content ?? ''
    );

    // LCP: in SSR/render iniziale mostriamo solo la prima slide (l'elemento LCP).
    // Le altre vengono aggiunte dopo il load della prima, per non competere con l'LCP.
    loadSecondarySlides: boolean = false;

    // Larghezze responsive generate per ogni immagine slider (vedi public/images/home_gallery/slider).
    private readonly slideVariantWidths: number[] = [480, 768];
    // Il main hero occupa col-md-8 (~66vw) da >=768px, altrimenti tutta la larghezza.
    protected readonly slideSizes: string = '(min-width: 768px) 66vw, 100vw';

    // Swipe variables
    private touchStartX: number = 0;
    private touchEndX: number = 0;
    private swipeThreshold: number = 50;

    // Mouse drag variables
    private mouseStartX: number = 0;
    private mouseEndX: number = 0;
    protected isDragging: boolean = false;
    private isActuallyDragging: boolean = false;
    private dragThreshold: number = 50;
    private clickTolerance: number = 10;

    // Overlay variables
    showOverlay: boolean = false;
    private overlayTimeout?: any;
    overlayDelayMs: number = 500;

    constructor() {
        // FIX 2: Esegui solo nel browser
        if (!this.appConfig.cli()) {
            // return; // Non fare nulla durante SSR
        }

        // LCP: in SSR iniettiamo il preload dell'immagine della prima slide
        // (elemento LCP della home) così il download parte subito, prima dell'hydration.
        if (this.appConfig.ssr()) {
            this.preloadFirstSlideImage();
        }

        afterNextRender(() => {
            // FIX 3: Esegui fuori da NgZone per evitare problemi di stabilità
            this.ngZone.runOutsideAngular(() => {
                this.initializeComponent();
            });
            
        });
    }
    
    // Costruisce lo srcset responsive da una slide usando le varianti generate
    // (es. 001-faac-480.webp 480w, 001-faac-768.webp 768w, 001-faac.webp 1472w).
    protected buildSrcset(slide: GallerySlideContent): string {
        const base = slide.imageUrl.replace(/\.webp$/i, '');
        const parts = this.slideVariantWidths
            .filter((w) => w < slide.width)
            .map((w) => `${base}-${w}.webp ${w}w`);
        parts.push(`${slide.imageUrl} ${slide.width}w`);
        return parts.join(', ');
    }

    private preloadFirstSlideImage(): void {
        const firstSlide = this.slides?.[0];
        if (!firstSlide?.imageUrl) {
            return;
        }
        const link = this.document.createElement('link');
        link.rel = 'preload';
        // setAttribute e non la property `as`: il DOM dell'SSR non la serializza e senza
        // as="image" il browser ignora il preload.
        link.setAttribute('as', 'image');
        link.setAttribute('fetchpriority', 'high');
        // Preload responsive: il browser sceglie la stessa variante dell'<img>,
        // evitando di scaricare l'originale a piena risoluzione su mobile.
        link.setAttribute('imagesrcset', this.buildSrcset(firstSlide));
        link.setAttribute('imagesizes', this.slideSizes);
        link.href = firstSlide.imageUrl; // fallback per browser senza imagesrcset
        this.document.head.appendChild(link);
    }

    // Banner 2 / 3 della slide corrente (aggiornati con il cambio slide: detectChanges in next/prevImage)
    protected sideImage(index: 0 | 1): GallerySideImage {
        return this.slides[this.currentIndex]?.sideImages?.[index] ?? this.defaultSideImages[index];
    }

    handleOverlayClick(event: MouseEvent): void {
        const anchor = (event.target as HTMLElement).closest('a');
        if (!anchor) return;

        const href = anchor.getAttribute('href');
        if (!href) return;

        if (href.startsWith('/') || href.startsWith('./')) {
            event.preventDefault();
            event.stopPropagation();
            this.router.navigateByUrl(href);
        }
    }

    private initializeComponent(): void {
        // FIX 4: Gestisci correttamente il caricamento dell'immagine
        if (this.currentGalleryImage?.nativeElement) {
            if (this.currentGalleryImage.nativeElement.complete) {
                this.initializeLayoutAndSlide();
            } else {
                this.imageLoadSubscription = fromEvent(this.currentGalleryImage.nativeElement, 'load')
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(() => {
                        this.ngZone.run(() => {
                            this.initializeLayoutAndSlide();
                        });
                    });
            }
        } else {
            // Fallback con timeout breve
            setTimeout(() => {
                if (this.currentGalleryImage?.nativeElement) {
                    this.ngZone.run(() => {
                        this.initializeLayoutAndSlide();
                    });
                }
            }, 100);
        }

        // FIX 5: Gestisci resize con debounce appropriato
        this.resizeSubscription = fromEvent(window, 'resize')
            .pipe(
                debounceTime(20), // Aggiungi debounce per performance
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.ngZone.run(() => {
                    this.adjustColumnHeights();
                });
            });
    }

    protected initializeLayoutAndSlide(): void {
        // LCP: la prima slide è caricata; ora possiamo aggiungere le altre.
        this.loadSecondarySlides = true;
        this.adjustColumnHeights();
        this.updateProcessedHtmlContent();
        this.startAutoSlide();
        this.scheduleOverlayDisplay();
    }

    ngOnDestroy(): void {
        // FIX 6: Pulizia completa di tutti i timer e subscriptions
        this.stopAutoSlide();
        this.clearOverlayTimeout();
        
        if (this.imageLoadSubscription) {
            this.imageLoadSubscription.unsubscribe();
        }
        if (this.resizeSubscription) {
            this.resizeSubscription.unsubscribe();
        }
        
        this.destroy$.next();
        this.destroy$.complete();
    }

    startAutoSlide(): void {
        if (!this.appConfig.cli() || this.slides.length <= 1) {
            return;
        }

        this.stopAutoSlide();

        // FIX 7: Esegui il timer fuori da NgZone.
        // Timer singolo per slide (nextImage → resetAutoSlide lo riarma) così ogni slide
        // può avere una durata propria tramite durationFactor.
        const durationMs = this.autoSlideIntervalMs * (this.slides[this.currentIndex]?.durationFactor ?? 1);
        this.ngZone.runOutsideAngular(() => {
            this.autoSlideSubscription = timer(durationMs)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.ngZone.run(() => {
                        this.nextImage();
                    });
                });
        });
    }

    stopAutoSlide(): void {
        if (this.autoSlideSubscription) {
            this.autoSlideSubscription.unsubscribe();
            this.autoSlideSubscription = null;
        }
    }

    resetAutoSlide(): void {
        this.stopAutoSlide();
        this.startAutoSlide();
    }

    nextImage(event?: Event): void {
        if (event?.type === 'click') {
            event.preventDefault();
        }

        this.hideOverlayImmediately();
        this.currentIndex = (this.currentIndex + 1) % this.slides.length;
        this.adjustColumnHeights();
        this.updateProcessedHtmlContent();
        this.resetAutoSlide();
        this.cdr.detectChanges();
        this.scheduleOverlayDisplay();
    }

    prevImage(event?: Event): void {
        if (event?.type === 'click') {
            event.preventDefault();
        }

        this.hideOverlayImmediately();
        this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
        this.adjustColumnHeights();
        this.updateProcessedHtmlContent();
        this.resetAutoSlide();
        this.cdr.detectChanges();
        this.scheduleOverlayDisplay();
    }

    private updateProcessedHtmlContent(): void {
        if (!this.appConfig.cli()) return;
        
        let currentHtml = this.slides[this.currentIndex].html_content;
        this.processedHtmlContent = this.sanitizer.bypassSecurityTrustHtml(currentHtml);
        this.cdr.detectChanges();
    }

    private scheduleOverlayDisplay(): void {
        if (!this.appConfig.cli()) return;
        
        this.clearOverlayTimeout();
        
        // FIX 8: Usa NgZone per i timeout
        this.ngZone.runOutsideAngular(() => {
            this.overlayTimeout = setTimeout(() => {
                this.ngZone.run(() => {
                    this.showOverlay = true;
                    this.resetAnimateCssClasses();
                });
            }, this.overlayDelayMs);
        });
    }

    private hideOverlayImmediately(): void {
        this.clearOverlayTimeout();
        this.showOverlay = false;
        this.removeAnimateCssClasses();
    }

    private clearOverlayTimeout(): void {
        if (this.overlayTimeout) {
            clearTimeout(this.overlayTimeout);
            this.overlayTimeout = null;
        }
    }

    private removeAnimateCssClasses(): void {
        if (!this.appConfig.cli() || !this.overlayContentRef?.nativeElement) {
            return;
        }

        const animatedElements = this.overlayContentRef.nativeElement.querySelectorAll('.animate__animated');
        animatedElements.forEach((el: HTMLElement) => {
            const classesToRemove = Array.from(el.classList).filter(c => c.startsWith('animate__') && c !== 'animate__animated');
            el.classList.remove(...classesToRemove);
            el.classList.remove('animate__animated');
        });
    }

    private resetAnimateCssClasses(): void {
        if (!this.appConfig.cli() || !this.overlayContentRef?.nativeElement) {
            return;
        }

        const animatedElements = this.overlayContentRef.nativeElement.querySelectorAll('.animate__animated');
        animatedElements.forEach((el: HTMLElement) => {
            const classesToRemove = Array.from(el.classList).filter(c => c.startsWith('animate__') && c !== 'animate__animated');
            el.classList.remove(...classesToRemove);
            el.classList.remove('animate__animated');
            
            // FIX 9: Usa requestAnimationFrame invece di setTimeout
            requestAnimationFrame(() => {
                el.classList.add('animate__animated');
                
                // Re-apply animation classes based on element type
                if (el.classList.contains('text-transform-none') && el.tagName === 'H6') {
                    el.classList.add('animate__fadeInUp', 'animate__delay-0-5s');
                } else if (el.classList.contains('h2') && el.tagName === 'P') {
                    el.classList.add('animate__fadeInUp', 'animate__delay-1s');
                } else if (el.classList.contains('custom-font') && el.tagName === 'SPAN') {
                    el.classList.add('animate__rollIn', 'animate__delay-1-5s');
                } else if (el.classList.contains('d-inline-block') && el.tagName === 'H4') {
                    el.classList.add('animate__fadeInDownBig', 'animate__delay-2s');
                }
            });
        });
    }

    // Touch handlers
    onTouchStart(event: TouchEvent): void {
        this.touchStartX = event.touches[0].clientX;
    }

    onTouchMove(event: TouchEvent): void {
        event.preventDefault();
        this.touchEndX = event.touches[0].clientX;
    }

    onTouchEnd(event: TouchEvent): void {
        this.touchEndX = event.changedTouches[0].clientX;
        const diff = this.touchStartX - this.touchEndX;

        if (Math.abs(diff) > this.swipeThreshold) {
            if (diff > 0) {
                this.nextImage();
            } else {
                this.prevImage();
            }
        }

        this.touchStartX = 0;
        this.touchEndX = 0;
    }

    // Mouse handlers
    onMouseDown(event: MouseEvent): void {
        if (event.button === 0) {
            this.isDragging = true;
            this.isActuallyDragging = false;
            this.mouseStartX = event.clientX;
            this.stopAutoSlide();
        }
    }

    onMouseMove(event: MouseEvent): void {
        if (this.isDragging) {
            this.mouseEndX = event.clientX;
            if (Math.abs(this.mouseStartX - this.mouseEndX) > this.clickTolerance) {
                this.isActuallyDragging = true;
            }
        }
    }

    onMouseUp(event: MouseEvent): void {
        if (this.isDragging) {
            this.isDragging = false;
            this.mouseEndX = event.clientX;

            if (this.isActuallyDragging) {
                const diff = this.mouseStartX - this.mouseEndX;
                if (Math.abs(diff) > this.dragThreshold) {
                    if (diff > 0) {
                        this.nextImage();
                    } else {
                        this.prevImage();
                    }
                }
                event.preventDefault();
                event.stopPropagation();
            }

            this.mouseStartX = 0;
            this.mouseEndX = 0;
            this.isActuallyDragging = false;
            this.startAutoSlide();
        }
    }

    onMouseLeave(event: MouseEvent): void {
        if (this.isDragging) {
            this.onMouseUp(event);
        }
        if (!this.isDragging) {
            this.startAutoSlide();
        }
    }

    onMouseEnter(): void {
        this.stopAutoSlide();
    }

    adjustColumnHeights(): void {
        if (!this.appConfig.cli()) return;

        if (window.innerWidth >= 768) {
            if (this.mainImageContainer) {
                let firstColImageHeight = 0;
                if (this.currentGalleryImage?.nativeElement?.complete) {
                    firstColImageHeight = this.currentGalleryImage.nativeElement.offsetHeight;
                } else if (this.mainImageContainer.nativeElement) {
                    firstColImageHeight = this.mainImageContainer.nativeElement.offsetHeight;
                }

                if (firstColImageHeight === 0) {
                    Log.warn('LayoutComponent: Main image height is 0. Cannot adjust secondary column heights correctly.');
                    return;
                }

                const halfHeight = firstColImageHeight / 2;
                const secondaryImageContainers = this.el.nativeElement.querySelectorAll('.secondary-image-container');

                secondaryImageContainers.forEach((imageContainer: HTMLElement) => {
                    this.renderer.setStyle(imageContainer, 'height', `${halfHeight - 8}px`);
                    this.renderer.setStyle(imageContainer, 'paddingTop', '0');
                    this.renderer.addClass(imageContainer, 'js-height-adjusted');
                });
            }
        } else {
            const secondaryImageContainers = this.el.nativeElement.querySelectorAll('.secondary-image-container');
            secondaryImageContainers.forEach((imageContainer: HTMLElement) => {
                this.renderer.removeStyle(imageContainer, 'height');
                this.renderer.setStyle(imageContainer, 'paddingTop', '0');
                this.renderer.setStyle(imageContainer, 'height', 'auto');
                
                const img = imageContainer.querySelector('img');
                if (img) {
                    this.renderer.setStyle(img, 'position', 'static');
                }
                
                this.renderer.addClass(imageContainer, 'js-height-adjusted');
            });
        }
    }
}